import {
  InternalType,
  CoreEntityDomainsOrganizationsEntrypointsApiV1SchemasResponseOrganization as OrganizationDto,
  MicrosoftTenantResponse as MicrosoftTenantDto,
  OrganizationResellerResponse as OrganizationResellerDto,
  OrganizationStatusEnum,
} from '@electricjs/core_entity-client';
import { Employee as EmployeeDto } from '@electricjs/core_entity-client';
import { Group as GroupDto } from '@electricjs/core_entity-client';

import {
  simpleEmployee,
  unknownEmployee1,
  unknownEmployee2,
} from '@/mocks/data/employeeData';

import {
  childGroup,
  dynamicGroup,
  grandparentGroup,
  hierarchicalOrganizationGroups,
  unusedGroupFixtures,
  hrisSyncedGroup,
  parentGroup,
  simpleGroup,
} from './groupData';

// Simple Org
export const simpleOrganization: OrganizationDto = {
  id: '67d26876-05a6-11ee-be56-0242ac120002',
  name: 'Simple Org',
  hris_integrations: [
    {
      id: '6695cb87-899d-4d3d-8a44-b872b01872e6',
      organization_id: '67d26876-05a6-11ee-be56-0242ac120002',
      hris_provider: {
        id: '6695cb87-899d-4d3d-8a44-b872b01872e6',
        name: 'BambooHR',
        slug: 'bamboohr',
      },
    },
  ],
  entitlements: [
    {
      id: 'integrations-entitlement-id',
      name: 'Integrations',
      slug: 'integrations',
    },
    {
      id: 'entitlement-xboardings',
      name: 'Onboardings & Offboardings',
      slug: 'xboardings',
    },
    {
      id: 'entitlement-applications',
      name: 'Applications',
      slug: 'applications',
    },
    {
      id: 'entitlement-employee-groups',
      name: 'Employee Groups',
      slug: 'employee_groups',
    },
    {
      id: 'entitlement-jumpcloud-mdm',
      name: 'MDM',
      slug: 'jumpcloud_mdm',
    },
    {
      id: 'entitlement-warehousing',
      name: 'Warehousing',
      slug: 'warehousing',
    },
    {
      id: 'entitlement-webhooks',
      name: 'Webhooks',
      slug: 'webhooks',
    },
    {
      id: 'entitlement-installments',
      name: 'Installments',
      slug: 'installments',
    },
    {
      id: 'entitlement-desktop-app',
      name: 'Desktop App',
      slug: 'desktop_app',
    },
    {
      id: 'entitlement-helpful-links',
      name: 'Helpful Links',
      slug: 'helpful_links',
    },
    {
      id: 'entitlement-support-center',
      name: 'Support Center',
      slug: 'support_center',
    },
    {
      id: 'entitlement-intake-form',
      name: 'Intake Form',
      slug: 'intake_form',
    },
    {
      id: 'entitlement-gigawatt',
      name: 'Gigawatt',
      slug: 'gigawatt',
    },
    {
      id: 'entitlement-gigawatt-basic',
      name: 'Gigawatt Basic',
      slug: 'gigawatt_basic',
    },
    {
      id: 'entitlement-marketplace',
      name: 'Marketplace',
      slug: 'marketplace',
    },
    {
      id: 'entitlement-offboarding-automation',
      name: 'Offboarding Automation',
      slug: 'offboarding_automation',
    },
    {
      id: 'entitlement-autopilot-device-enrollment',
      name: 'Autopilot Device Enrollment',
      slug: 'autopilot_device_enrollment',
    },
    {
      id: 'entitlement-harmony-email-and-collaboration-security-dashboard',
      name: 'Harmony Email & Collaboration Security',
      slug: 'harmony_email_and_collaboration_security_dashboard',
    },
    {
      id: 'entitlement-threatdown-security-dashboard',
      name: 'ThreatDown Security Dashboard',
      slug: 'threatdown_security_dashboard',
    },
    {
      id: 'entitlement-hris-integration',
      name: 'HRIS Integration',
      slug: 'hris_integration',
    },
  ],
  internal_type: InternalType.Test,
  salesforce_id: '626bf336-7f18-4e70-ac3a-da2322c7907d',
  status: OrganizationStatusEnum.Active,
};

export const simpleOrganizationEmployees: EmployeeDto[] = [simpleEmployee];

export const simpleOrganizationGroups: GroupDto[] = [
  simpleGroup,
  dynamicGroup,
  hrisSyncedGroup,
  grandparentGroup,
  parentGroup,
  childGroup,
  ...hierarchicalOrganizationGroups,
  ...unusedGroupFixtures,
];

// Connected Org (connected to HRIS)
export const connectedOrganization: OrganizationDto = {
  id: 'connected-org-id',
  name: 'Connected Org',
  hris_integrations: [
    {
      id: 'hris-id',
      organization_id: '67d26876-05a6-11ee-be56-0242ac120013',
      last_fetched_at: '2023-08-30T14:00:00.000Z',
      hris_provider: {
        id: '6695cb87-899d-4d3d-8a44-b872b01872e6',
        name: 'BambooHR',
        slug: 'bamboohr',
      },
    },
  ],
  entitlements: [
    {
      id: '67d26876-05a6-11ee-be56-0242ac120013',
      name: 'HRIS Integration',
      slug: 'hris_integration',
    },
  ],
  internal_type: InternalType.Test,
  salesforce_id: '626bf336-7f18-4e70-ac3a-da2322c7907d',
  status: OrganizationStatusEnum.Active,
};

export const connectedOrganizationEmployees: EmployeeDto[] = [
  simpleEmployee,
  unknownEmployee1,
  unknownEmployee2,
];

export const buildMockMicrosoftTenants = (): MicrosoftTenantDto[] => [
  {
    id: 'microsoft-tenant-1',
    organization_id: simpleOrganization.id,
    name: 'Simple Org Microsoft',
    ms_tenant_id: '8c4b1d5e-2f3a-4b6c-9d7e-1a2b3c4d5e6f',
    domain: 'simple-org.onmicrosoft.com',
    created_at: '2026-05-01T00:00:00Z',
    updated_at: '2026-05-01T00:00:00Z',
  },
];

export const buildMockOrganizationResellers = (): OrganizationResellerDto[] => [
  {
    id: 'organization-reseller-1',
    abm_reseller_id: '123456',
    country_code: 'US',
  },
];
